import { useEffect, useRef, useState } from "react";
import { getCsrfHeader } from "../../../lib/csrf";
import { createSerialPoller, extractPairingCode } from "./whatsappSetup";
import styles from "./WhatsAppSetupModal.module.css";

type Props = {
  open: boolean;
  // The tenant whose OpenClaw we wire the WhatsApp channel onto.
  tenantId: string;
  // Integration title for copy (e.g. "WhatsApp").
  title: string;
  // "setup": the full first-time walkthrough. "reconnect": WhatsApp unlinked a
  // number that was working, so only the scan is needed - everyone already
  // approved keeps their access and nothing is paired again.
  mode?: "setup" | "reconnect";
  // Whether the channel is already connected for this instance. When false (a
  // first-time setup) we hide the "already set up, skip ahead" shortcuts, since
  // we now know it hasn't been set up yet.
  alreadyConnected?: boolean;
  // Linking runs as the instance owner, so only the owner can start it.
  // Everyone else can still pair their own number on a linked agent.
  canLink?: boolean;
  onClose: () => void;
  // Called after the channel state changes (started / paired) so the detail
  // page can refetch and reflect the new status.
  onChanged: () => void;
};

// Step model for the guided WhatsApp channel flow. WhatsApp has no bot token —
// the agent links to a dedicated WhatsApp account by scanning a QR (Baileys /
// WhatsApp Web linked device), then the customer's own number is paired in via a
// code. The scan step talks to ztc-core (start + poll link status); the pair
// step approves the code.
type StepKey = "number" | "scan" | "message" | "pair" | "done";

// How far ztc-core's background setup job has got. The browser does not drive
// any of this: it starts the job and reads its phase. Enabling the channel,
// restarting the gateway, following WhatsApp's code rotation and healing a
// stalled listener all happen server-side.
type Phase =
  | "Queued"
  | "Preparing"
  | "Restarting"
  | "AwaitingQr"
  | "AwaitingScan"
  | "Linking"
  | "Listening"
  | "Expired"
  | "Failed";

type StatusReply = { phase: Phase; qrPayload: string | null; message: string | null };

// Gap between status polls. /status is a single indexed row read that never
// touches the tenant, so this is about UI responsiveness only - but see
// createSerialPoller: polls are chained, never fired on an interval. It sits
// well inside the 20 seconds WhatsApp gives each code.
const POLL_INTERVAL_MS: number = 2_500;

// What each phase says to someone watching. Deliberately about their agent, not
// about our machinery.
const PHASE_HINT: Record<Phase, string> = {
  Queued: "Getting started…",
  Preparing: "Switching WhatsApp on…",
  Restarting: "Restarting your agent. About a minute…",
  AwaitingQr: "Generating your code…",
  AwaitingScan: "Waiting for a scan…",
  Linking: "Linked. Finishing the first sync…",
  Listening: "Linked and ready.",
  Expired: "That code wasn't scanned in time.",
  Failed: "",
};

// Phases where the job is still working towards a code.
const WORKING: ReadonlySet<Phase> = new Set<Phase>(["Queued", "Preparing", "Restarting", "AwaitingQr"]);

// Phases a job can still be in when the modal is reopened. Picking one of these
// up again shows the live code instead of starting over.
const IN_FLIGHT: ReadonlySet<Phase> = new Set<Phase>(["Preparing", "Restarting", "AwaitingQr", "AwaitingScan", "Linking"]);

const SETUP_STEPS: ReadonlyArray<{ key: StepKey; label: string }> = [
  { key: "number", label: "Get a number" },
  { key: "scan", label: "Scan to link" },
  { key: "message", label: "Message agent" },
  { key: "pair", label: "Pair device" },
  { key: "done", label: "Done" },
];

const RECONNECT_STEPS: ReadonlyArray<{ key: StepKey; label: string }> = [
  { key: "scan", label: "Scan to reconnect" },
  { key: "done", label: "Done" },
];

// Adding another person to an agent that is already linked: no number and no
// scan, just their message and the code it gets back.
const PAIR_STEPS: ReadonlyArray<{ key: StepKey; label: string }> = [
  { key: "message", label: "Message agent" },
  { key: "pair", label: "Pair device" },
  { key: "done", label: "Done" },
];

type Flow = "setup" | "reconnect" | "pair";

const FLOW_STEPS: Record<Flow, ReadonlyArray<{ key: StepKey; label: string }>> = {
  setup: SETUP_STEPS,
  reconnect: RECONNECT_STEPS,
  pair: PAIR_STEPS,
};

// Shown wherever we set expectations: WhatsApp unlinks linked devices when the
// main phone goes quiet, and that is the one thing the customer can prevent.
const STAY_LINKED_NOTE = (
  <>
    <strong>Keep the agent's phone on</strong> and open WhatsApp on it weekly. WhatsApp unlinks devices after
    about two weeks of quiet. If it does, you'll see Reconnect here.
  </>
);

// Per-step GIF + caption for the steps that have a recording. The scan step is
// special: its media slot shows the live QR / link status instead of a GIF.
const STEP_MEDIA: Record<StepKey, { src: string | null; caption: string }> = {
  number: { src: "/integrations/whatsapp/step-number.gif", caption: "Adding the agent's number as a second WhatsApp account on one phone" },
  scan: { src: null, caption: "Scanning the link code from WhatsApp > Linked devices" },
  message: { src: "/integrations/whatsapp/step-message.gif", caption: "Messaging the agent to get a pairing code" },
  pair: { src: "/integrations/whatsapp/step-pair.gif", caption: "Copying the pairing code from your chat" },
  done: { src: "/integrations/whatsapp/step-done.gif", caption: "Your agent replying in WhatsApp" },
};

const StepMedia = ({ step }: { step: StepKey }): JSX.Element => {
  const media = STEP_MEDIA[step];
  const [errored, setErrored] = useState<boolean>(false);
  useEffect(() => {
    setErrored(false);
  }, [step]);
  return (
    <div className={styles.media}>
      {media.src && !errored ? (
        <img className={styles.mediaImg} src={media.src} alt={media.caption} onError={() => setErrored(true)} />
      ) : (
        <div className={styles.mediaPlaceholder}>
          <span className={styles.mediaTag}>GIF</span>
          <span className={styles.mediaCaption}>{media.caption}</span>
        </div>
      )}
    </div>
  );
};

// The media slot once linking has finished: a plain panel with a green tick.
const DoneMedia = ({ caption }: { caption: string }): JSX.Element => (
  <div className={styles.media}>
    <div className={`${styles.mediaPlaceholder} ${styles.mediaDone}`}>
      <span className={styles.doneMark} aria-hidden>
        ✓
      </span>
      <span className={styles.mediaCaption}>{caption}</span>
    </div>
  </div>
);

// Scan-step media: the QR to scan (a data: URL) while one is live, otherwise a
// placeholder naming what the agent is doing. The server swaps the code the
// moment WhatsApp rotates it, so what is shown here is always scannable.
const ScanMedia = ({ phase, qrPayload, started }: { phase: Phase; qrPayload: string | null; started: boolean }): JSX.Element => {
  if (started && phase === "AwaitingScan" && qrPayload && qrPayload.startsWith("data:")) {
    return (
      <div className={styles.media}>
        <img className={styles.mediaImg} src={qrPayload} alt="WhatsApp linking QR code" />
      </div>
    );
  }
  if (started && phase === "Listening") return <DoneMedia caption={PHASE_HINT.Listening} />;
  const idle = !started || phase === "Failed" || phase === "Expired";
  const caption = !started ? "No code yet." : phase === "Failed" ? "We couldn't generate a code." : PHASE_HINT[phase];
  return (
    <div className={styles.media}>
      <div className={styles.mediaPlaceholder}>
        <span className={styles.mediaTag}>{idle ? "QR" : "···"}</span>
        <span className={styles.mediaCaption}>{caption}</span>
      </div>
    </div>
  );
};

// Guided, self-serve WhatsApp *channel* setup (integration.channel='whatsapp').
// Distinct from WhatsApp-the-Composio-business-app: this gives the agent its own
// WhatsApp number, links it by QR, and pairs the customer's number so they can
// chat with the agent like a friend.
export const WhatsAppSetupModal = ({
  open,
  tenantId,
  title,
  mode = "setup",
  alreadyConnected = false,
  canLink = true,
  onClose,
  onChanged,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Starts as the mode the modal was opened in; "Pair another number" switches
  // to the short pairing flow without closing the modal.
  const [flow, setFlow] = useState<Flow>(mode);
  const steps = FLOW_STEPS[flow];
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [pairingCode, setPairingCode] = useState<string>("");
  // Scan step. The browser holds only what it renders: the job's phase, the QR
  // the job published, and any failure text. It computes nothing about the
  // channel's real state - ztc-core owns that.
  const [phase, setPhase] = useState<Phase>("Queued");
  // Whether a run is ours to watch: /start was ACKNOWLEDGED, or reopening the
  // modal found one still going. The poll below must not run before that: a
  // poll issued while /start is in flight reads the PREVIOUS run's row, QR and
  // all, so a stale code would appear and then vanish a second later.
  const [started, setStarted] = useState<boolean>(false);
  const [qrPayload, setQrPayload] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // Latches true once the job has EVER reported Listening, so Continue stays
  // enabled even if a later poll is momentarily behind.
  const [everListening, setEverListening] = useState<boolean>(false);
  // Pair step.
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [pairError, setPairError] = useState<string | null>(null);
  const [pairPending, setPairPending] = useState<boolean>(false);

  const goTo = (key: StepKey): void => {
    const index = steps.findIndex((s) => s.key === key);
    if (index >= 0) setStepIndex(index);
  };

  const pairAnother = (): void => {
    setFlow("pair");
    setStepIndex(0);
    setPairingCode("");
    setPairError(null);
    setPairPending(false);
  };

  const readStatus = async (signal?: AbortSignal): Promise<StatusReply | null> => {
    const res = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/whatsapp/status`, {
      credentials: "include",
      ...(signal !== undefined ? { signal } : {}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StatusReply;
  };

  // Queue the setup job (202, never touches the box) and move to the scan step.
  // Doubles as Try again and Get a new code: the server supersedes any run in
  // flight rather than starting a second one.
  const startChannel = async (): Promise<void> => {
    goTo("scan");
    setPhase("Queued");
    setStarted(false);
    setQrPayload(null);
    setEverListening(false);
    setStartError(null);
    try {
      const res = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/whatsapp/start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ mode: mode === "reconnect" ? "relink" : "setup" }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Couldn't start WhatsApp (${res.status})`);
      }
      setStarted(true);
      onChanged();
    } catch (err) {
      setPhase("Failed");
      setStarted(true);
      setStartError(err instanceof Error ? err.message : String(err));
    }
  };

  // On open: pick up a run that is still going (the customer closed the modal
  // mid-scan and came back). Otherwise a reconnect starts straight away, since
  // asking for one is the only reason to be here.
  const resumeOrBegin = async (): Promise<void> => {
    try {
      const data = await readStatus();
      if (data !== null && IN_FLIGHT.has(data.phase)) {
        setPhase(data.phase);
        setQrPayload(data.qrPayload);
        setStarted(true);
        goTo("scan");
        return;
      }
    } catch {
      // Nothing to resume; fall through.
    }
    if (mode === "reconnect" && canLink) await startChannel();
  };

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setFlow(mode);
      setStepIndex(0);
      setPairingCode("");
      setPhase("Queued");
      setStarted(false);
      setQrPayload(null);
      setEverListening(false);
      setStartError(null);
      setSubmitting(false);
      setPairError(null);
      setPairPending(false);
      void resumeOrBegin();
    } else if (!open && d.open) {
      d.close();
    }
    // resumeOrBegin reads only props that stay fixed while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const step: StepKey = steps[stepIndex]?.key ?? "scan";

  // Poll the setup job while the scan step is open.
  //
  // Polls are CHAINED, never fired on an interval: createSerialPoller schedules
  // the next request only once the previous one has settled. The old setInterval
  // kept firing regardless, and when each poll shelled OpenClaw's CLI onto the
  // tenant over SSM, requests overlapped and the box was buried. /status is a
  // cheap DB read now, but a self-limiting client is what stops the next
  // expensive endpoint quietly becoming the same bug. Each poll also tells the
  // server someone is still looking, which is what keeps new codes coming.
  useEffect(() => {
    if (!open || step !== "scan" || !started || everListening) return;
    let cancelled = false;
    const ac = new AbortController();

    const poller = createSerialPoller(async () => {
      try {
        const data = await readStatus(ac.signal);
        if (cancelled) return false;
        if (data === null) return true;
        setPhase(data.phase);
        setQrPayload(data.qrPayload);
        setStartError(data.phase === "Failed" ? (data.message ?? "Setup didn't finish.") : null);
        if (data.phase === "Listening") {
          setEverListening(true);
          return false;
        }
        // Expired and Failed are terminal until they start again, which
        // remounts this poll.
        return data.phase !== "Failed" && data.phase !== "Expired";
      } catch {
        // Transient (offline, aborted, a blip). Keep polling.
        return true;
      }
    }, POLL_INTERVAL_MS);

    poller.start();
    return () => {
      cancelled = true;
      ac.abort();
      poller.stop();
    };
    // readStatus only reads tenantId, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, started, everListening, tenantId]);

  if (!open) return null;

  const codeCandidate = extractPairingCode(pairingCode);

  const close = (): void => {
    onChanged();
    onClose();
  };

  const submitPairing = async (): Promise<void> => {
    const code = extractPairingCode(pairingCode);
    if (code === null) return;
    setSubmitting(true);
    setPairError(null);
    setPairPending(false);
    try {
      const res = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/whatsapp/pair`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ pairingCode: code }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Pairing failed (${res.status})`);
      }
      const data = (await res.json()) as { status: string };
      if (data.status === "approved") {
        onChanged();
        goTo("done");
      } else {
        setPairPending(true);
      }
    } catch (err) {
      setPairError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const linked: boolean = everListening;
  // The job is still working towards a code; the customer should wait rather
  // than navigate. Failed and Expired are NOT busy - they can start again.
  //
  // Gated on `started` because "Queued" is also the resting phase before any
  // run exists. Without it, busy is true with nothing running, which disables
  // Back, Continue AND the Escape key.
  const busy: boolean = started && WORKING.has(phase);
  const ownerOnly = <p className={styles.hintWarn}>Only the agent's owner can link WhatsApp. Ask them to do it.</p>;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!busy && !submitting) close();
      }}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <h2 className={styles.title}>
            {flow === "reconnect" ? `Reconnect ${title}` : flow === "pair" ? "Pair another number" : `Set up ${title} as a channel`}
          </h2>
          <ol className={`${styles.steps} ${steps.length <= 2 ? styles.stepsCompact : ""}`}>
            {steps.map((s, i) => (
              <li
                key={s.key}
                className={`${styles.stepDot} ${
                  i === stepIndex ? styles.stepDotActive : i < stepIndex ? styles.stepDotDone : ""
                }`}
              >
                <span className={styles.stepNum}>{i < stepIndex ? "✓" : i + 1}</span>
                <span className={styles.stepLabel}>{s.label}</span>
              </li>
            ))}
          </ol>
        </header>

        <div className={styles.split}>
          <div className={styles.body}>
            {step === "number" ? (
              <>
                <p className={styles.lead}>
                  Your agent needs its own WhatsApp number.
                </p>
                <ol className={styles.stepList}>
                  <li className={styles.stepItem}>
                    Get a second number. An eSIM is easiest: it only needs to receive one SMS.
                  </li>
                  <li className={styles.stepItem}>
                    In WhatsApp, go to <strong>Settings → Account → Add account</strong> and add the
                    agent's number. A separate phone works too.
                  </li>
                  <li className={styles.stepItem}>That account is your agent. You'll link it next.</li>
                </ol>
                <p className={styles.note}>{STAY_LINKED_NOTE}</p>
                {alreadyConnected ? (
                  <p className={styles.altPath}>
                    Adding someone? Have them message your agent, then paste the code it sends them.
                  </p>
                ) : null}
                {!canLink ? ownerOnly : null}
              </>
            ) : null}

            {step === "scan" ? (
              <>
                <p className={styles.lead}>
                  {flow === "reconnect"
                    ? "WhatsApp unlinked your agent. Scan the code to link it again."
                    : "Link your agent by scanning the code."}
                </p>
                <ol className={styles.stepList}>
                  <li className={styles.stepItem}>
                    On the agent's phone, open WhatsApp and go to{" "}
                    <strong>Settings → Linked devices → Link a device</strong>.
                  </li>
                  <li className={styles.stepItem}>Scan the code. It refreshes every 20 seconds.</li>
                </ol>
                {flow === "reconnect" ? (
                  <p className={styles.note}>Everyone you'd approved keeps their access. Nothing needs pairing again.</p>
                ) : null}
                {!started ? (
                  canLink ? (
                    <button
                      type="button"
                      className={`${styles.primaryBtn} ${styles.inlineAction}`}
                      onClick={() => void startChannel()}
                    >
                      Show a linking code
                    </button>
                  ) : (
                    ownerOnly
                  )
                ) : linked ? (
                  <p className={`${styles.status} ${styles.statusLinked}`}>{PHASE_HINT.Listening}</p>
                ) : phase === "Expired" ? (
                  <>
                    <p className={`${styles.status} ${styles.statusExpired}`}>{PHASE_HINT.Expired}</p>
                    <button
                      type="button"
                      className={`${styles.primaryBtn} ${styles.inlineAction}`}
                      onClick={() => void startChannel()}
                    >
                      Get a new code
                    </button>
                  </>
                ) : phase !== "Failed" ? (
                  <p className={`${styles.status} ${phase === "Linking" ? styles.statusLinked : ""}`}>{PHASE_HINT[phase]}</p>
                ) : null}
                {startError ? (
                  <>
                    <div className={styles.errorBanner}>{startError}</div>
                    {canLink ? (
                      <button
                        type="button"
                        className={`${styles.secondaryBtn} ${styles.inlineAction}`}
                        onClick={() => void startChannel()}
                      >
                        Try again
                      </button>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}

            {step === "message" ? (
              <>
                {flow === "pair" ? (
                  <>
                    <p className={styles.lead}>Have the new person message your agent.</p>
                    <ol className={styles.stepList}>
                      <li className={styles.stepItem}>
                        From their own WhatsApp, they send any message to the agent's number.
                      </li>
                      <li className={styles.stepItem}>
                        It replies with a <strong>pairing code</strong>. Get that code from them.
                      </li>
                    </ol>
                  </>
                ) : (
                  <>
                    <p className={styles.lead}>Now message your agent so it knows who you are.</p>
                    <ol className={styles.stepList}>
                      <li className={styles.stepItem}>
                        From your personal WhatsApp, send any message to the agent's number.
                      </li>
                      <li className={styles.stepItem}>
                        It replies with a <strong>pairing code</strong>. Copy it for the next step.
                      </li>
                    </ol>
                    <p className={styles.warnNote}>
                      <strong className={styles.warnEmphasis}>No reply yet?</strong> Your agent can take up to
                      five minutes to finish connecting, and may restart once. Wait a couple of minutes and
                      message it again.
                    </p>
                  </>
                )}
              </>
            ) : null}

            {step === "pair" ? (
              <>
                <p className={styles.lead}>Paste the pairing code your agent sent you.</p>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Pairing code</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={pairingCode}
                    onChange={(e) => {
                      setPairingCode(e.target.value);
                      setPairError(null);
                      setPairPending(false);
                    }}
                    placeholder="e.g. 4FHTUTB8"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={submitting}
                  />
                </label>
                {pairPending ? (
                  <p className={styles.hint}>
                    We don't see that code yet. Make sure you messaged your agent on WhatsApp, then
                    try again.
                  </p>
                ) : pairingCode.trim() !== "" && codeCandidate === null ? (
                  <p className={styles.hintWarn}>
                    That doesn't look like a pairing code. It's a short code like{" "}
                    <code className={styles.code}>4FHTUTB8</code>.
                  </p>
                ) : (
                  <p className={styles.hint}>Pasting its whole reply is fine. We'll pick the code out.</p>
                )}
                {pairError ? <div className={styles.errorBanner}>{pairError}</div> : null}
              </>
            ) : null}

            {step === "done" ? (
              <>
                {flow === "pair" ? (
                  <p className={`${styles.lead} ${styles.leadDone}`}>
                    <span className={styles.leadTick} aria-hidden>
                      ✓
                    </span>
                    <span>
                      <strong>Paired.</strong> That number can message your agent now.
                    </span>
                  </p>
                ) : flow === "reconnect" ? (
                  <p className={`${styles.lead} ${styles.leadDone}`}>
                    <span className={styles.leadTick} aria-hidden>
                      ✓
                    </span>
                    <span>
                      <strong>You're back.</strong> Everyone you'd approved can keep messaging your agent.
                    </span>
                  </p>
                ) : (
                  <>
                    <p className={`${styles.lead} ${styles.leadDone}`}>
                      <span className={styles.leadTick} aria-hidden>
                        ✓
                      </span>
                      <span>
                        <strong>You're connected.</strong> Message your agent on WhatsApp any time.
                      </span>
                    </p>
                  </>
                )}
                <p className={styles.note}>{STAY_LINKED_NOTE}</p>
              </>
            ) : null}
          </div>
          <div className={styles.mediaCol}>
            {step === "scan" ? (
              <ScanMedia phase={phase} qrPayload={qrPayload} started={started} />
            ) : step === "done" && flow === "reconnect" ? (
              // The setup recording shows a pairing chat, which a reconnect skips.
              <DoneMedia caption="WhatsApp is linked again." />
            ) : (
              <StepMedia step={step} />
            )}
          </div>
        </div>

        <footer className={styles.footer}>
          {step === "number" ? (
            <>
              <button type="button" className={`${styles.secondaryBtn} ${styles.footerLead}`} onClick={close}>
                Cancel
              </button>
              {alreadyConnected ? (
                <button type="button" className={styles.secondaryBtn} onClick={pairAnother}>
                  Pair another number
                </button>
              ) : null}
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void startChannel()}
                disabled={!canLink}
              >
                I've got a number
              </button>
            </>
          ) : null}

          {step === "scan" ? (
            <>
              {flow === "reconnect" ? (
                <button
                  type="button"
                  className={`${styles.secondaryBtn} ${styles.footerLead}`}
                  onClick={close}
                  disabled={busy}
                >
                  Close
                </button>
              ) : (
                <button type="button" className={styles.secondaryBtn} onClick={() => goTo("number")} disabled={busy}>
                  Back
                </button>
              )}
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => goTo(flow === "reconnect" ? "done" : "message")}
                disabled={busy || !linked}
              >
                Continue<span aria-hidden> →</span>
              </button>
            </>
          ) : null}

          {step === "message" ? (
            <>
              {flow === "pair" ? (
                <button type="button" className={`${styles.secondaryBtn} ${styles.footerLead}`} onClick={close}>
                  Close
                </button>
              ) : (
                <button type="button" className={styles.secondaryBtn} onClick={() => goTo("scan")}>
                  Back
                </button>
              )}
              <button type="button" className={styles.primaryBtn} onClick={() => goTo("pair")}>
                I have a pairing code
              </button>
            </>
          ) : null}

          {step === "pair" ? (
            <>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => goTo("message")}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void submitPairing()}
                disabled={submitting || codeCandidate === null}
              >
                {submitting ? "Pairing…" : "Pair device"}
              </button>
            </>
          ) : null}

          {step === "done" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={pairAnother}>
                Pair another number
              </button>
              <button type="button" className={styles.primaryBtn} onClick={close}>
                Done
              </button>
            </>
          ) : null}
        </footer>
      </div>
    </dialog>
  );
};
